import React, { useEffect, useState } from 'react'
import SlideBase from './SlideBase'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import FadeBackground from '../FadeBackground'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import { WRAPPED_SLIDES } from '../../config/slides'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import useVideoBackgroundWithFallback from '../../hooks/useVideoBackgroundWithFallback'

// Get slide config for this slide (static, doesn't depend on runtime data)
const SLIDE_CONFIG = WRAPPED_SLIDES.find(s => s.id === 'offer-views')
const SLIDE_ID = 'offer-views' as const

function OfferViewsSlide() {
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const totalsData = useWrappedStorySelector(state => state.totalsData)

  // Use the video background with fallback hook
  const { background, isBackgroundReady, isCurrentSlide } = useVideoBackgroundWithFallback({
    slideConfig: SLIDE_CONFIG,
    slideId: SLIDE_ID,
  })

  const [animate, setAnimate] = useState(!SLIDE_CONFIG?.video) // If no video, animate immediately

  // Totals-only mode is determined by not having user wrapped data
  const isTotalsOnlyMode = !data

  // Start animation when background is ready (video loaded or fallback image)
  useEffect(() => {
    if (SLIDE_CONFIG?.video && isBackgroundReady && isCurrentSlide) {
      setAnimate(true)
    }
  }, [isBackgroundReady, isCurrentSlide])

  // Need either user data or totals data to render
  if (!data && !totalsData) return null

  const totalOffersViewed = totalsData?.stats.offersViewed ?? 0
  const averageOffersViewed = totalsData?.stats.averageOffersViewed ?? data?.stats.averageOffersViewed ?? 0

  // Show totals version if in totals-only mode
  if (isTotalsOnlyMode) {
    return (
      <SlideBase background={background}>
        {animate && <FadeBackground>
          <Group direction="vertical" grow verticalAlign="end" horizontalAlign="start" gap={12}>
            <Group direction="vertical" gap={4}>
              <Caption colour="highlight-primary" variant="small" format="uppercase">Offers Viewed</Caption>
              <Heading variant="heading5">
                <b>
                  In 2025, Luxury Escapes travellers explored
                </b>
              </Heading>
            </Group>
            <Heading variant="heading1alt">
              <FormatDecimal value={totalOffersViewed} /> offers
            </Heading>
          </Group>
        </FadeBackground>}
      </SlideBase>
    )
  }

  return (
    <SlideBase background={background}>
      {animate && <FadeBackground>
        <Group direction="vertical" grow verticalAlign="end" horizontalAlign="start" gap={12}>
          <Group direction="vertical" gap={4}>
            <Caption colour="highlight-primary" variant="small" format="uppercase">Offers Viewed</Caption>
            <Heading variant="heading5">
              <b>
                You found inspiration in
              </b>
            </Heading>
          </Group>
          <Group direction="vertical" gap={4}>
            <Heading variant="heading1alt">
              <FormatDecimal value={data?.stats.offersViewed ?? 0} /> offers
            </Heading>
            <BodyText variant="medium">
              On average, our travellers viewed <b><FormatDecimal value={averageOffersViewed} /> offers</b> last year.
            </BodyText>
          </Group>
        </Group>
      </FadeBackground>}
    </SlideBase>
  )
}

export default React.memo(OfferViewsSlide)
